import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { CustomerLoyaltyCard } from '@src/components/CustomerLoyaltyCard/CustomerLoyaltyCard'
import { Helmet } from '@src/components/Helmet'
import { allOutletDiscountsQuery } from '@src/hooks/sharedQueries/useAllDiscounts/queries/__generated__/getAllDiscounts.graphql-interface'
import { useOutletLoyaltyCards } from '../../../hooks/useOutletLoyaltyCards'

import { MenuGroup } from './MenuGroup/MenuGroup'
import { MenuItemSearchResults } from './MenuItemSearchResults/MenuItemSearchResults'
import {
  DealsAndOffersContainer,
  SingleDiscountContainer,
} from './OutletMenuStyles'

import { CustomerDetailsAndAddressesQuery } from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/queries/__generated__/CustomerDetailsAndAddressesQuery.graphql-interface'
import { AddOnItemsOffer } from '../OutletDiscounts/AddOnItemsOffer'
import { SingleDiscount } from '../OutletDiscounts/SingleDiscount'
import { useOutletPageContext } from '../OutletPageContext'
import { DealsAndOffersSkeleton } from '../OutletPageSkeleton/DealsAndOffersSkeleton'
import { GetAddOnItemMenuGroupsForOutletQuery } from '../queries/__generated__/GetAddOnItemMenuGroupsForOutlet.graphql-interface'
import { ScrollToContainer } from './MenuGroup/MenuGroup.styles'

interface MenuSectionProps {
  menuId: string
  children: React.ReactNode
  setActiveMenuId: (id: string) => void
  manualNavigation: boolean
  showMenuSearchResults: boolean
  isListening: boolean
  rootMargin: string
}

// Used for the active menu section capsule highlight and navigation
const MenuSection: React.FC<MenuSectionProps> = ({
  menuId,
  children,
  setActiveMenuId,
  manualNavigation,
  showMenuSearchResults,
  isListening,
  rootMargin,
}) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin,
  })

  useEffect(() => {
    if (inView && !manualNavigation && isListening && !showMenuSearchResults)
      setActiveMenuId(menuId)
  }, [
    inView,
    menuId,
    setActiveMenuId,
    manualNavigation,
    isListening,
    showMenuSearchResults,
  ])

  return (
    <div ref={ref} id={menuId}>
      {children}
    </div>
  )
}

interface OutletMenuProps {
  allDiscounts: allOutletDiscountsQuery | undefined
  customerDetails: CustomerDetailsAndAddressesQuery | undefined
  addOnItemMenuGroups: GetAddOnItemMenuGroupsForOutletQuery | undefined
  addOnItemMenuGroupsLoading: boolean
  customerDetailsLoading: boolean
  discountsLoading: boolean
}

export const OutletMenu: React.FC<OutletMenuProps> = ({
  allDiscounts,
  discountsLoading,
  customerDetails,
  customerDetailsLoading,
  addOnItemMenuGroups,
  addOnItemMenuGroupsLoading,
}) => {
  const {
    outletFulfilment: { outlet },
    setActiveMenuId,
    treeForModalData,
    manualNavigation,
    showMenuSearchResults,
    setManualNavigation,
  } = useOutletPageContext()

  const allowAddOnItems = outlet.restaurant?.allowAddOnItems ?? false
  const { loyaltyCards, isOutletInBasket, basketTotals } =
    useOutletLoyaltyCards(customerDetails)
  const scrollStartY = useRef<number>(0)
  const [isListening, setIsListening] = useState(false)

  const handleScroll = useCallback(() => {
    if (!manualNavigation || !isListening) return

    const currentScrollY = window.scrollY
    const scrollDelta = Math.abs(currentScrollY - scrollStartY.current)

    if (scrollDelta > 50) {
      setManualNavigation(false)
      setIsListening(false)
    }
  }, [manualNavigation, isListening, setManualNavigation])

  useEffect(() => {
    if (manualNavigation) {
      scrollStartY.current = window.scrollY
      setIsListening(false)
    }

    const manualScrollTimeout = setTimeout(() => {
      setIsListening(true)
    }, 800)

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      clearTimeout(manualScrollTimeout)
      setIsListening(false)
    }
  }, [manualNavigation, handleScroll])

  // All the whacky logic above is to strike a balance between manual and scroll navigation observer ranges
  // Tight detection during manual nav - prevents "overshooting" small and narrow sections when setting active menu
  const rootMargin = manualNavigation
    ? '-100px 0px -80% 0px'
    : '-100px 0px -60% 0px'

  const hasDealsSection =
    !!(
      allDiscounts?.allDiscountsByOutletId?.length ||
      loyaltyCards?.length ||
      addOnItemMenuGroups?.menuItemGroupsForOutlet?.length
    ) && !showMenuSearchResults

  const renderedMenuGroups = useMemo(
    () =>
      treeForModalData.map(outletMenuGroup =>
        outletMenuGroup.parentMenu ? (
          <MenuSection
            key={outletMenuGroup.parentMenu.id}
            menuId={outletMenuGroup.parentMenu.id}
            setActiveMenuId={setActiveMenuId}
            manualNavigation={manualNavigation}
            showMenuSearchResults={showMenuSearchResults}
            isListening={isListening}
            rootMargin={rootMargin}
          >
            <MenuGroup
              outletId={outlet.id}
              outletParentMenu={outletMenuGroup.parentMenu}
              outletSubMenus={outletMenuGroup.subMenus}
            />
          </MenuSection>
        ) : null
      ),
    [
      treeForModalData,
      outlet.id,
      setActiveMenuId,
      manualNavigation,
      showMenuSearchResults,
      isListening,
      rootMargin,
    ]
  )

  return (
    <>
      <Helmet title={`${outlet.displayName} Menu`} />
      {discountsLoading ||
      customerDetailsLoading ||
      addOnItemMenuGroupsLoading ? (
        <DealsAndOffersSkeleton />
      ) : hasDealsSection ? (
        <MenuSection
          menuId="discountMenuItem"
          setActiveMenuId={setActiveMenuId}
          manualNavigation={manualNavigation}
          showMenuSearchResults={showMenuSearchResults}
          isListening={isListening}
          rootMargin={rootMargin}
        >
          <ScrollToContainer id="discountMenuItem" aria-hidden />
          <DealsAndOffersContainer>
            <SingleDiscountContainer>
              {loyaltyCards.map(loyaltyCard => (
                <CustomerLoyaltyCard
                  loyaltyCard={loyaltyCard}
                  key={loyaltyCard.id}
                  basketTotals={
                    isOutletInBasket && basketTotals ? basketTotals : undefined
                  }
                />
              ))}
              {allDiscounts?.allDiscountsByOutletId.map(discount => (
                <div key={discount.id}>
                  <SingleDiscount allDiscounts={discount} />
                </div>
              ))}
              {allowAddOnItems &&
                addOnItemMenuGroups?.menuItemGroupsForOutlet
                  ?.filter(menuGroup => menuGroup.outletMenuItems?.length > 0)
                  ?.map(menuGroup => (
                    <AddOnItemsOffer
                      key={menuGroup.id}
                      parentMenuName={menuGroup.name}
                      addOnItemsMenuMinimumBasketTotal={
                        menuGroup.addOnItemsMenuMinimumBasketTotal
                      }
                    />
                  ))}
            </SingleDiscountContainer>
          </DealsAndOffersContainer>
        </MenuSection>
      ) : null}
      {showMenuSearchResults ? (
        <MenuItemSearchResults outletId={outlet.id} />
      ) : (
        renderedMenuGroups
      )}
    </>
  )
}
